<template>
  <div class="recruitment-app">
    <div class="bg-blur-circle"></div>

    <div class="workspace-container">
      <header class="page-header">
        <div class="title-section">
          <h1>面试官工作台</h1>
          <p>当前日期：{{ activeDate }} · 待办面试 ({{ filteredCandidates.length }})</p>
        </div>
        <el-button type="primary" icon="el-icon-plus" round size="medium">安排面试</el-button>
      </header>

      <section class="calendar-wrapper">
        <div class="calendar-inner">
          <button class="nav-btn" @click="changeWeek(-1)"><i class="el-icon-arrow-left"></i></button>

          <div class="date-list">
            <div v-for="item in weekDays" :key="item.date" :class="['date-card', { active: activeDate === item.date }]"
              @click="handleDateClick(item.date)">
              <span class="week">{{ item.weekName }}</span>
              <span class="day">{{ item.day }}</span>
              <i class="indicator" v-if="hasInterview(item.date)"></i>
            </div>
          </div>

          <button class="nav-btn" @click="changeWeek(1)"><i class="el-icon-arrow-right"></i></button>
        </div>
      </section>

      <section class="list-wrapper">
        <transition-group name="staggered-fade" tag="div">
          <div v-for="(user, index) in filteredCandidates" :key="user.id" class="interview-card"
            :style="{ '--delay': index * 0.1 + 's' }">
            <div class="card-left">
              <div class="avatar-box" :class="user.status">
                {{ user.name.charAt(0) }}
              </div>
              <div class="main-info">
                <div class="name-box">
                  <span class="name">{{ user.name }}</span>
                  <span class="status-badge" :class="user.status">
                    {{ statusMap[user.status].label }}
                  </span>
                </div>
                <div class="job-tag">{{ user.job }}</div>
              </div>
            </div>

            <div class="card-mid">
              <div class="meta-item">
                <i class="el-icon-suitcase"></i> {{ user.exp }}经验
              </div>
              <div class="meta-item">
                <i class="el-icon-collection-tag"></i> {{ user.edu }}
              </div>
            </div>

            <div class="card-right">
              <span class="time-stamp">面试时间：{{ user.applyTime }}</span>
              <el-button class="action-btn" type="primary" plain @click="openDetail(user)">
                查看登记表
              </el-button>
            </div>
          </div>
        </transition-group>

        <div v-if="filteredCandidates.length === 0" class="empty-state">
          <img src="https://img.icons8.com/bubbles/200/calendar.png" alt="empty" />
          <p>今日清闲，暂无面试安排</p>
        </div>
      </section>
    </div>

    <el-dialog :visible.sync="dialogVisible" width="850px" top="8vh" custom-class="modern-dialog" :show-close="false">
      <div slot="title" class="dialog-header">
        <div class="header-user">
          <span class="avatar-mini">{{ currentUser.name ? currentUser.name.charAt(0) : '' }}</span>
          <div class="title-txt">
            <h3>{{ currentUser.name }} 的面试档案</h3>
            <span>应聘岗位：{{ currentUser.job }}</span>
          </div>
        </div>
        <el-button icon="el-icon-close" circle @click="dialogVisible = false"></el-button>
      </div>

      <div class="dialog-body">
        <el-tabs v-model="activeTab">
          <el-tab-pane label="个人登记表" name="resume">
            <div class="resume-scroll">
              <el-descriptions :column="2" border>
                <el-descriptions-item label="姓名">{{ currentUser.name }}</el-descriptions-item>
                <el-descriptions-item label="手机号">138-xxxx-8888</el-descriptions-item>
                <el-descriptions-item label="期望城市">上海/远程</el-descriptions-item>
                <el-descriptions-item label="工作年限">{{ currentUser.exp }}</el-descriptions-item>
                <el-descriptions-item label="技能标签" :span="2">
                  <el-tag size="mini" style="margin-right:5px">Vue2/3</el-tag>
                  <el-tag size="mini" style="margin-right:5px">TypeScript</el-tag>
                  <el-tag size="mini">Node.js</el-tag>
                </el-descriptions-item>
                <el-descriptions-item label="项目经验" :span="2">
                  负责公司核心业务系统的架构升级，将首屏加载速度提升了 40%。
                </el-descriptions-item>
              </el-descriptions>
            </div>
          </el-tab-pane>

          <el-tab-pane label="面试评价" name="eval">
            <div class="eval-grid">
              <div class="eval-form-side">
                <el-form :model="evalForm" size="small">
                  <el-form-item label="专业评分">
                    <el-rate v-model="evalForm.score" show-text></el-rate>
                  </el-form-item>
                  <el-form-item label="录用决策">
                    <el-select v-model="evalForm.result" style="width: 100%">
                      <el-option label="推进至下一轮" value="pass"></el-option>
                      <el-option label="暂时不匹配" value="reject"></el-option>
                      <el-option label="放入人才库" value="pool"></el-option>
                    </el-select>
                  </el-form-item>
                  <el-form-item label="面试反馈">
                    <el-input type="textarea" :rows="4" v-model="evalForm.comment"
                      placeholder="请输入对候选人的客观评价..."></el-input>
                  </el-form-item>
                  <el-button type="primary" block @click="submitEval" style="width:100%">提交并存档</el-button>
                </el-form>
              </div>
              <div class="eval-history-side">
                <h4>历史面议记录</h4>
                <el-timeline>
                  <el-timeline-item v-for="(rec, index) in historyRecords" :key="index" :timestamp="rec.time"
                    color="#409EFF">
                    <div class="history-item">
                      <strong>{{ rec.interviewer }}</strong>
                      <p>{{ rec.content }}</p>
                    </div>
                  </el-timeline-item>
                </el-timeline>
              </div>
            </div>
          </el-tab-pane>
        </el-tabs>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import dayjs from 'dayjs';

export default {
  data() {
    return {
      currentFirstDay: dayjs().startOf('week').add(1, 'day'),
      activeDate: dayjs().format('YYYY-MM-DD'),
      weekDays: [],
      candidates: [
        { id: 1, name: '陈子涵', job: '高级前端开发', exp: '5年', edu: '本科', applyTime: '2026-01-11', status: 'interviewing' },
        { id: 2, name: '林静雯', job: 'UI 设计师', exp: '3年', edu: '硕士', applyTime: '2026-01-11', status: 'new' },
        { id: 3, name: '周杰明', job: '后端技术专家', exp: '8年', edu: '博士', applyTime: '2026-01-13', status: 'hired' },
        { id: 4, name: '王梦', job: '产品经理', exp: '4年', edu: '本科', applyTime: '2026-01-11', status: 'hired' },
      ],
      statusMap: {
        interviewing: { label: '初试', type: 'warning' },
        new: { label: '复试', type: 'primary' },
        hired: { label: '终试', type: 'success' }
      },
      dialogVisible: false,
      activeTab: 'resume',
      currentUser: {},
      evalForm: { score: 5, comment: '', result: 'pass' },
      historyRecords: [
        { time: '2026-01-11 09:30', interviewer: '技术一面-张工', content: '算法基础很好，对工程化有深入见解。' }
      ]
    };
  },
  computed: {
    filteredCandidates() {
      return this.candidates.filter(c => c.applyTime === this.activeDate);
    }
  },
  mounted() { this.generateWeek(); },
  methods: {
    generateWeek() {
      const days = [];
      const weekNames = ['周一', '周二', '周三', '周四', '周五', '周六', '周日'];
      for (let i = 0; i < 7; i++) {
        const dateObj = this.currentFirstDay.add(i, 'day');
        days.push({
          weekName: weekNames[i],
          day: dateObj.format('DD'),
          date: dateObj.format('YYYY-MM-DD')
        });
      }
      this.weekDays = days;
    },
    hasInterview(date) {
      return this.candidates.some(c => c.applyTime === date);
    },
    changeWeek(offset) {
      this.currentFirstDay = this.currentFirstDay.add(offset * 7, 'day');
      this.generateWeek();
    },
    handleDateClick(date) { this.activeDate = date; },
    openDetail(user) {
      this.currentUser = user;
      this.dialogVisible = true;
    },
    submitEval() {
      if (!this.evalForm.comment) return this.$message.warning('写点评价吧');
      this.historyRecords.unshift({
        time: dayjs().format('YYYY-MM-DD HH:mm'),
        interviewer: '面试官-您',
        content: this.evalForm.comment
      });
      this.$message.success('评价提交成功');
      this.evalForm.comment = '';
    }
  }
};
</script>

<style lang="scss">
// 全局变量
$slate-900: #0f172a;
$slate-700: #334155;
$slate-400: #94a3b8;
$primary: #6366f1;
$bg-main: #f1f5f9;

* {
  box-sizing: border-box;
}

.recruitment-app {
  background: $bg-main;
  min-height: calc(100vh - 50px);
  padding: 40px 20px;
  font-family: 'PingFang SC', 'Microsoft YaHei', sans-serif;
  position: relative;
  overflow: hidden;

  .bg-blur-circle {
    position: absolute;
    top: -100px;
    right: -100px;
    width: 400px;
    height: 400px;
    background: radial-gradient(circle, rgba(99, 102, 241, 0.1) 0%, transparent 70%);
  }

  .workspace-container {
    width: 100%;
    max-width: 800px;
    margin: 0 auto;
    position: relative;
  }

  // 标题
  .page-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    margin-bottom: 30px;

    h1 {
      font-size: 24px;
      color: $slate-900;
      margin: 0;
    }

    p {
      color: $slate-400;
      margin-top: 5px;
      font-size: 14px;
    }
  }

  // 日历美化
  .calendar-wrapper {
    background: #ffffffaa;
    backdrop-filter: blur(10px);
    border-radius: 20px;
    padding: 15px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.03);
    margin-bottom: 25px;

    .calendar-inner {
      display: flex;
      align-items: center;
      justify-content: space-between;

      .nav-btn {
        background: none;
        border: none;
        cursor: pointer;
        color: $slate-400;
        font-size: 18px;

        &:hover {
          color: $primary;
        }
      }

      .date-list {
        display: flex;
        gap: 10px;

        .date-card {
          width: 55px;
          height: 75px;
          border-radius: 14px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s;

          .week {
            font-size: 11px;
            color: $slate-400;
            margin-bottom: 5px;
          }

          .day {
            font-size: 18px;
            font-weight: bold;
            color: $slate-700;
          }

          .indicator {
            width: 4px;
            height: 4px;
            background: $primary;
            border-radius: 50%;
            margin-top: 6px;
          }

          &.active {
            background: $primary;
            transform: scale(1.1);

            .week,
            .day {
              color: #fff;
            }

            .indicator {
              background: #fff;
            }
          }

          &:hover:not(.active) {
            background: #fff;
            box-shadow: 0 4px 10px rgba(0, 0, 0, 0.05);
          }
        }
      }
    }
  }

  // 候选人卡片美化
  .interview-card {
    background: #fff;
    border-radius: 16px;
    padding: 20px;
    margin-bottom: 15px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    transition: 0.3s;
    border: 1px solid #f1f5f9;
    animation: slideUp 0.5s ease forwards;
    animation-delay: var(--delay);
    opacity: 0;

    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 10px 25px rgba(0, 0, 0, 0.06);
    }

    .card-left {
      display: flex;
      align-items: center;
      gap: 15px;
      width: 220px;

      .avatar-box {
        width: 48px;
        height: 48px;
        border-radius: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: bold;
        font-size: 18px;

        &.new {
          background: #e0e7ff;
          color: #4338ca;
        }

        &.interviewing {
          background: #fef3c7;
          color: #b45309;
        }

        &.hired {
          background: #dcfce7;
          color: #15803d;
        }
      }

      .name-box {
        display: flex;
        align-items: center;
        gap: 8px;

        .name {
          font-weight: 600;
          color: $slate-900;
        }

        .status-badge {
          font-size: 10px;
          padding: 2px 6px;
          border-radius: 4px;

          &.new {
            background: #e0e7ff;
            color: #4338ca;
          }

          &.interviewing {
            background: #fff7ed;
            color: #ea580c;
          }

          &.hired {
            background: #ecfdf5;
            color: #059669;
          }
        }
      }

      .job-tag {
        font-size: 12px;
        color: $slate-400;
        margin-top: 4px;
      }
    }

    .card-mid {
      display: flex;
      gap: 20px;

      .meta-item {
        font-size: 13px;
        color: $slate-700;
        display: flex;
        align-items: center;
        gap: 4px;

        i {
          color: $slate-400;
        }
      }
    }

    .card-right {
      text-align: right;

      .time-stamp {
        display: block;
        font-size: 11px;
        color: #cbd5e1;
        margin-bottom: 6px;
      }

      .action-btn {
        border-radius: 8px;
        font-weight: 500;
      }
    }
  }

  // 弹窗样式重写
  .modern-dialog {
    border-radius: 20px;
    overflow: hidden;

    .el-dialog__header {
      padding: 0;
    }

    .dialog-header {
      padding: 20px 25px;
      background: $slate-900;
      color: #fff;
      display: flex;
      justify-content: space-between;
      align-items: center;

      .header-user {
        display: flex;
        align-items: center;
        gap: 15px;

        .avatar-mini {
          width: 40px;
          height: 40px;
          background: #334155;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
        }

        .title-txt {
          h3 {
            margin: 0;
            font-size: 16px;
          }

          span {
            font-size: 12px;
            color: $slate-400;
          }
        }
      }

      .el-button {
        background: rgba(255, 255, 255, 0.1);
        border: none;
        color: #fff;

        &:hover {
          background: rgba(255, 255, 255, 0.2);
        }
      }
    }

    .dialog-body {
      padding: 10px 25px 25px;

      .el-tabs__item {
        font-weight: 500;
        padding: 0 20px;
      }

      .resume-scroll {
        padding: 15px 0;
        max-height: 400px;
        overflow-y: auto;
      }

      .eval-grid {
        display: grid;
        grid-template-columns: 1.2fr 1fr;
        gap: 30px;
        padding-top: 15px;

        .eval-form-side {
          background: #f8fafc;
          padding: 20px;
          border-radius: 15px;
        }

        .eval-history-side {
          h4 {
            margin: 0 0 15px;
            color: $slate-700;
            font-size: 14px;
          }

          .history-item {
            font-size: 13px;

            strong {
              color: $slate-900;
              display: block;
              margin-bottom: 3px;
            }

            p {
              color: $slate-400;
              margin: 0;
            }
          }
        }
      }
    }
  }

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }

    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .empty-state {
    text-align: center;
    padding: 60px 0;
    color: $slate-400;

    img {
      filter: grayscale(1);
      opacity: 0.5;
      margin-bottom: 20px;
    }
  }
}
</style>